import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getInvoiceDetailOptions, getInvoiceItemsOptions } from '@/lib/client/@tanstack/react-query.gen'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

export const Route = createFileRoute('/link/$parentId/invoices/$invoiceId')({
  component: InvoiceDetailPage,
})

function InvoiceDetailPage() {
  const { parentId, invoiceId } = Route.useParams()

  const { data: invoiceDetail, isLoading } = useQuery({
    ...getInvoiceDetailOptions({
      path: { parentId, invoiceId },
    }),
  })

  const { data: invoiceItems, isLoading: itemsLoading } = useQuery({
    ...getInvoiceItemsOptions({
      path: { id: invoiceId },
    }),
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    )
  }

  if (!invoiceDetail) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-red-600">Facture introuvable</div>
      </div>
    )
  }

  const invoice = invoiceDetail.invoice
  const paymentAllocations = invoiceDetail.paymentAllocations

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PAID':
        return <Badge className="bg-green-500">Payée</Badge>
      case 'PARTIALLY_PAID':
        return <Badge className="bg-yellow-500">Partiellement payée</Badge>
      case 'PENDING':
        return <Badge className="bg-orange-500">En attente</Badge>
      case 'OVERDUE':
        return <Badge className="bg-red-500">En retard</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CARD':
        return 'Carte bancaire'
      case 'SEPA':
        return 'Prélèvement SEPA'
      case 'BANK_TRANSFER':
        return 'Virement bancaire'
      case 'MANUAL':
        return 'Manuel'
      default:
        return method
    }
  }

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return <Badge className="bg-green-500 text-xs">Réussi</Badge>
      case 'PENDING':
        return <Badge className="bg-orange-500 text-xs">En attente</Badge>
      case 'FAILED':
        return <Badge className="bg-red-500 text-xs">Échoué</Badge>
      default:
        return <Badge className="text-xs">{status}</Badge>
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/link/$parentId/invoices" params={{ parentId }}>
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour aux factures
            </Button>
          </Link>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold">{invoice.invoiceNumber}</h1>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(invoice.status)}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${import.meta.env.VITE_API_URL}/api/public/invoices/${invoice.id}/pdf`, '_blank')}
            >
              <Download className="mr-2 h-4 w-4" />
              Télécharger PDF
            </Button>
          </div>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations de la facture</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Période de facturation</p>
                  <p className="font-medium">
                    {format(new Date(invoice.billingPeriodStart), 'dd MMMM yyyy', { locale: fr })} -{' '}
                    {format(new Date(invoice.billingPeriodEnd), 'dd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date d'échéance</p>
                  <p className="font-medium">
                    {format(new Date(invoice.dueDate), 'dd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Montant total</p>
                  <p className="text-lg font-bold">{invoice.totalAmount.toFixed(2)} €</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Montant payé</p>
                  <p className="text-lg font-bold text-green-600">{invoice.paidAmount.toFixed(2)} €</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Reste à payer</p>
                  <p className="text-lg font-bold text-orange-600">{invoice.remainingAmount.toFixed(2)} €</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Détail des cours</CardTitle>
            </CardHeader>
            <CardContent>
              {itemsLoading ? (
                <p className="text-sm text-gray-500">Chargement...</p>
              ) : invoiceItems && invoiceItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Élève</TableHead>
                      <TableHead>École</TableHead>
                      <TableHead>Matière</TableHead>
                      <TableHead>Créneau</TableHead>
                      <TableHead className="text-right">Heures</TableHead>
                      <TableHead className="text-right">Prix/heure</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceItems.map((item: any, idx: number) => (
                      <>
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.studentName}</TableCell>
                          <TableCell className="text-sm">{item.studentSchool}</TableCell>
                          <TableCell>{item.courseSubject}</TableCell>
                          <TableCell className="text-sm">{item.courseTimeSlot}</TableCell>
                          <TableCell className="text-right">{item.hours}h</TableCell>
                          <TableCell className="text-right">{item.unitPricePerHour?.toFixed(2)} €</TableCell>
                          <TableCell className="text-right font-medium">{item.totalAmount?.toFixed(2)} €</TableCell>
                        </TableRow>
                        {item.attendanceDetails && item.attendanceDetails.length > 0 && (
                          <TableRow key={`${idx}-details`}>
                            <TableCell colSpan={7} className="bg-gray-50 text-xs text-gray-600 py-2">
                              <div className="space-y-1">
                                {item.attendanceDetails.map((attendance: any, attIdx: number) => (
                                  <div key={attIdx} className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {format(new Date(attendance.date), 'dd/MM/yyyy', { locale: fr })}:
                                    </span>
                                    <span className="text-gray-700">{attendance.status}</span>
                                    {attendance.hours > 0 && (
                                      <span className="text-gray-700">{attendance.hours}h</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-gray-500">Aucun cours facturé</p>
              )}
            </CardContent>
          </Card>

          {paymentAllocations && paymentAllocations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Paiements alloués</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Date</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Montant</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Méthode</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Statut</th>
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentAllocations.map((allocation) => (
                        <tr key={allocation.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 text-sm">
                            {format(new Date(allocation.createdAt), 'dd MMM yyyy', { locale: fr })}
                          </td>
                          <td className="py-3 text-sm font-medium">
                            {allocation.allocatedAmount.toFixed(2)} €
                          </td>
                          <td className="py-3 text-sm">
                            {getPaymentMethodLabel(allocation.paymentMethod)}
                          </td>
                          <td className="py-3">
                            {getPaymentStatusBadge(allocation.paymentStatus)}
                          </td>
                          <td className="py-3">
                            <Link
                              to="/link/$parentId/payments/$paymentId"
                              params={{ parentId, paymentId: allocation.paymentId }}
                            >
                              <Button variant="link" size="sm" className="p-0 h-auto">
                                Voir détails
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
